import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Shared section header used across the entire lender-pack UI (pre AND post
 * lodgement).  Every section now has:
 *   ▸ a coloured top accent strip that visually caps the card
 *   ▸ a soft neutral header background (very subtle tint) so cards no longer
 *     compete with each other for attention
 *   ▸ a compact STEP chip, section icon, title, subtitle
 *   ▸ optional right-hand pill and collapsible chevron
 */
export function ZoneHeader({
  step, icon: Icon, title, subtitle,
  accent, headerBg, border, ink, mutedInk,
  right = null, internal = false,
  collapsible = false, collapsed = false, onToggle,
  testid, showTopStrip = true, compact = false,
}) {
  const clickable = collapsible && onToggle;
  const header = (
    <div className={`flex items-center justify-between gap-2 flex-wrap ${compact ? 'px-4 py-2' : 'px-5 py-3.5'}`} style={{ background: headerBg, colorScheme: 'light' }}>
      <div className={`flex items-center min-w-0 flex-1 ${compact ? 'gap-2' : 'gap-3'}`}>
        {/* Small compact STEP chip */}
        <div className={`${compact ? 'w-7 h-7' : 'w-9 h-9'} rounded-lg flex flex-col items-center justify-center shrink-0`}
          style={{ background: accent, boxShadow: '0 2px 6px rgba(15, 23, 42, 0.10)' }}>
          <div className={`${compact ? 'text-[5.5px]' : 'text-[6.5px]'} font-black uppercase tracking-[0.14em] leading-none`} style={{ color: 'rgba(255,255,255,0.7)' }}>STEP</div>
          <div className={`${compact ? 'text-[11px]' : 'text-[13px]'} font-black tabular-nums leading-none mt-0.5`} style={{ color: '#ffffff' }}>{step}</div>
        </div>
        {/* Outlined icon square (less visually heavy than solid) */}
        <div className={`${compact ? 'w-7 h-7' : 'w-8 h-8'} rounded-lg flex items-center justify-center shrink-0`}
          style={{ background: '#ffffff', border: `1.5px solid ${border}` }}>
          {Icon && <Icon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} style={{ color: accent }} />}
        </div>
        {/* Title + subtitle */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`${compact ? 'text-[13px]' : 'text-[15px]'} font-black leading-tight truncate`} style={{ color: ink }}>{title}</h3>
            {internal && (
              <span className="text-[8.5px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ background: accent, color: '#ffffff' }}>
                Internal only
              </span>
            )}
          </div>
          <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-semibold mt-0.5 leading-snug line-clamp-2`} style={{ color: mutedInk }}>{subtitle}</p>
        </div>
      </div>
      <div className={`flex items-center flex-wrap justify-end shrink-0 ${compact ? 'gap-2' : 'gap-3'}`}>
        {right}
        {collapsible && (collapsed
          ? <ChevronDown className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} style={{ color: mutedInk }} />
          : <ChevronUp className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} style={{ color: mutedInk }} />)}
      </div>
    </div>
  );

  return (
    <div className="border-b" style={{ borderColor: border }}>
      {showTopStrip && <div className={compact ? 'h-0.5' : 'h-1'} style={{ background: accent }} />}
      {clickable ? (
        <button type="button" onClick={onToggle} className="w-full text-left transition-colors hover:brightness-95" data-testid={testid}>
          {header}
        </button>
      ) : (
        <div data-testid={testid}>{header}</div>
      )}
    </div>
  );
}

// Standard palettes for zones — headerBg is now very light so headers don't
// compete for attention; the coloured top-strip + STEP chip carry the accent.
export const ZONE_STYLE = {
  sla:            { accent: '#2563eb', headerBg: '#f8fafc', border: '#bfdbfe', ink: '#0f172a', mutedInk: '#475569' },
  nextAction:     { accent: '#d97706', headerBg: '#f8fafc', border: '#fcd34d', ink: '#0f172a', mutedInk: '#475569' },
  mirs:           { accent: '#4f46e5', headerBg: '#f8fafc', border: '#c7d2fe', ink: '#0f172a', mutedInk: '#475569' },
  timeline:       { accent: '#0f172a', headerBg: '#f8fafc', border: '#cbd5e1', ink: '#0f172a', mutedInk: '#475569' },
  packContents:   { accent: '#334155', headerBg: '#f8fafc', border: '#cbd5e1', ink: '#0f172a', mutedInk: '#475569' },
  // Pre-lodgement section variants — keyed by section.id so PackBuilder can use them too
  notes:             { accent: '#2563eb', headerBg: '#f8fafc', border: '#bfdbfe', ink: '#0f172a', mutedInk: '#475569' },
  documents:         { accent: '#0d9488', headerBg: '#f8fafc', border: '#5eead4', ink: '#0f172a', mutedInk: '#475569' },
  checklist:         { accent: '#4f46e5', headerBg: '#f8fafc', border: '#c7d2fe', ink: '#0f172a', mutedInk: '#475569' },
  qc:                { accent: '#9333ea', headerBg: '#f8fafc', border: '#d8b4fe', ink: '#0f172a', mutedInk: '#475569', internal: true },
  internal_comments: { accent: '#dc2626', headerBg: '#f8fafc', border: '#fca5a5', ink: '#0f172a', mutedInk: '#475569', internal: true },
  signoff:           { accent: '#059669', headerBg: '#f8fafc', border: '#6ee7b7', ink: '#0f172a', mutedInk: '#475569', internal: true },
};

// Tone overrides for the Next Action zone (colour matches urgency).
export const NEXT_ACTION_TONE = {
  slate:   { accent: '#475569', headerBg: '#f8fafc', border: '#e2e8f0', ink: '#0f172a', mutedInk: '#475569' },
  blue:    { accent: '#2563eb', headerBg: '#f8fafc', border: '#bfdbfe', ink: '#0f172a', mutedInk: '#475569' },
  amber:   { accent: '#d97706', headerBg: '#fffbeb', border: '#fcd34d', ink: '#0f172a', mutedInk: '#475569' },
  rose:    { accent: '#e11d48', headerBg: '#fff1f2', border: '#fda4af', ink: '#0f172a', mutedInk: '#475569' },
  emerald: { accent: '#059669', headerBg: '#f8fafc', border: '#6ee7b7', ink: '#0f172a', mutedInk: '#475569' },
  teal:    { accent: '#0d9488', headerBg: '#f8fafc', border: '#5eead4', ink: '#0f172a', mutedInk: '#475569' },
  purple:  { accent: '#9333ea', headerBg: '#f8fafc', border: '#d8b4fe', ink: '#0f172a', mutedInk: '#475569' },
  indigo:  { accent: '#4f46e5', headerBg: '#f8fafc', border: '#c7d2fe', ink: '#0f172a', mutedInk: '#475569' },
};
